<template>
  <div
    class="report-panel-container"
    :class="{ 'pdf-export-mode': isExportingPdf }"
  >
    <!-- 左侧报告记录 -->
    <div class="report-list-section" v-loading="isLoadingList">
      <!-- 当有报告数据时，显示此部分 -->
      <template v-if="reports.length > 0">
        <!-- 顶部预览大图 -->
        <div class="report-preview">
          <template v-if="activeReport">
            <img :src="getThumbnailUrl(activeReport)" alt="Report Preview" />
            <div
              v-if="isThumbnailConverting(activeReport)"
              class="report-thumb-loading"
            >
              <i class="el-icon-loading"></i>
            </div>
          </template>
        </div>

        <!-- 文件名 -->
        <div class="active-report-header">
          <div v-if="activeReport" class="report-name">
            {{ reportTitle }}
          </div>
          <span
            v-if="activeReport"
            class="status-badge"
            :class="statusClass(activeReport.status)"
          >
            {{ $t(`status.${activeReport.status}`) }}
          </span>
        </div>

        <!-- 可左右滑动的小图列表 -->
        <div
          class="thumbnail-scroll-wrapper"
          ref="scrollWrapper"
          @mousedown="handleMouseDown"
          @mousemove="handleMouseMove"
          @mouseup="handleMouseUp"
          @mouseleave="handleMouseLeave"
        >
          <div class="thumbnail-list">
            <div
              v-for="report in reports"
              :key="report.image_id"
              class="thumbnail-item"
              :class="{
                active:
                  activeReport && activeReport.image_id === report.image_id,
              }"
              @click="setActiveReport(report)"
            >
              <div class="image-container">
                <img :src="getThumbnailUrl(report)" :alt="report.image_name" />
                <div
                  v-if="isThumbnailConverting(report)"
                  class="report-thumb-loading report-thumb-loading--compact"
                >
                  <i class="el-icon-loading"></i>
                </div>
              </div>
              <div
                v-if="report.status === 'failed'"
                class="thumbnail-error-overlay"
              >
                <i class="el-icon-error"></i>
              </div>
            </div>
          </div>
        </div>

        <!-- 分割线 -->
        <div class="divider"></div>

        <!-- 分割线下方的文本菜单 -->
        <ul class="report-menu">
          <li
            v-for="item in currentMenuItems"
            :key="item"
            :class="{ active: activeMenu === item }"
            @click="handleMenuClick(item)"
          >
            {{ item }}
          </li>
        </ul>

        <!-- 底部的上传/下载按钮 -->
        <div class="report-actions">
          <el-button
            type="primary"
            icon="el-icon-upload2"
            @click="triggerUpload"
            >{{ $t("main.uploadArea.buttonText") }}</el-button
          >

          <div class="upload-usage-hint">
            {{ $t("main.uploadArea.uploadVolume") }}{{ imageUploadCount }} /
            {{ maxUploadPerDay }}
          </div>

          <el-button
            v-if="canExportReport"
            type="primary"
            icon="el-icon-download"
            @click="openDownloadDialog"
            >{{ $t("sidebar.downloadReport") }}</el-button
          >
        </div>
      </template>

      <!-- 当没有报告数据时，显示此部分 -->
      <div v-else class="no-report-view">
        <el-empty :description="$t('main.noData')" :image-size="100"></el-empty>
      </div>
    </div>

    <!-- 右侧报告详情 -->
    <div
      class="report-detail-section"
      v-loading="isLoadingDetail"
      ref="reportDetailSectionRef"
    >
      <div v-if="activeReport && reportDetailData" class="detail-wrapper">
        <div class="detail-header">
          <div class="header-title-group">
            <h3 v-if="currentMenuItems.length > 0" class="main-title">
              {{ currentMenuItems[0] }}
            </h3>
            <!-- <p class="sub-title">{{ activeReport.image_name }}</p> -->
          </div>
          <el-button type="primary" class="original-file-btn">
            {{ reportDetailData.image_name }}
          </el-button>
        </div>
        <div class="detail-content">
          <el-scrollbar class="detail-scrollbar">
            <component
              :is="detailComponent"
              :report-data="reportDetailData"
              ref="detailComponentRef"
            ></component>
          </el-scrollbar>
        </div>
      </div>
      <div v-else class="report-detail-empty">
        <el-empty :description="$t('main.noData')" :image-size="200"></el-empty>
      </div>
    </div>

    <!-- 下载报告弹窗 -->
    <el-dialog
      :title="$t('sidebar.downloadReport')"
      :visible.sync="downloadDialogVisible"
      class="download-dialog"
    >
      <div class="dialog-content">
        <!-- 全选 Checkbox -->
        <el-checkbox
          :indeterminate="isIndeterminate"
          v-model="checkAll"
          @change="handleCheckAllChange"
          class="all-report-checkbox"
        >
          {{ $t("sidebar.allReport") }}
        </el-checkbox>

        <!-- 报告列表滚动区 -->
        <el-scrollbar class="report-list-scrollbar" :style="scrollbarStyle">
          <div
            v-for="(report, index) in downloadableReports"
            :key="report.image_id"
            class="report-item"
          >
            <!-- 旧代码：报告选择复选框（已移除） -->
            <!-- <el-checkbox
              :value="checkedReports.includes(report.image_id)"
              class="report-item-checkbox"
              @change="toggleReportSelection(report.image_id)"
            >
              <span class="report-index">{{
                String.fromCharCode(65 + index)
              }}</span>
            </el-checkbox> -->
            <!-- 新代码：只显示索引，不显示选择框 -->
            <span class="report-index">{{
              String.fromCharCode(65 + index)
            }}</span>
            <div class="download-thumb-wrapper">
              <img :src="getThumbnailUrl(report)" class="report-thumbnail" />
              <div
                v-if="isThumbnailConverting(report)"
                class="report-thumb-loading report-thumb-loading--compact"
              >
                <i class="el-icon-loading"></i>
              </div>
            </div>
            <span class="report-image-name">{{ report.image_name }}</span>

            <el-checkbox-group
              v-model="downloadTypes[report.image_id]"
              class="download-type-radio"
              @click.native.stop
            >
              <el-checkbox label="pdf">PDF</el-checkbox>
              <el-checkbox label="csv">CSV</el-checkbox>
            </el-checkbox-group>
          </div>
        </el-scrollbar>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button
          type="primary"
          icon="el-icon-download"
          @click="handleDownload"
          :disabled="hasNoSelections || isDownloading"
          :loading="isDownloading"
        >
          {{
            isDownloading
              ? $t("sidebar.downloadReporting")
              : $t("sidebar.downloadReport")
          }}
        </el-button>
      </span>
    </el-dialog>

    <!-- 进度条遮罩层组件 -->
    <task-progress-overlay
      :visible="isDownloading"
      :progress="displayProgress"
      :message="downloadProgressMessage"
    />
  </div>
</template>

<script>
import { getAnalysisDetail, getUserInfo, downloadReport } from "@/api";
import UTIF from "utif";
import { mapGetters } from "vuex";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
// 导入所有报告详情组件
import ColorReproductionReportDetail from "./report-details/ColorReproductionReportDetail.vue";
import DynamicRangeReportDetail from "./report-details/DynamicRangeReportDetail.vue";
import SharpnessReportDetail from "./report-details/SharpnessReportDetail.vue";
import LuminanceUniformityReportDetail from "./report-details/LuminanceUniformityReportDetail.vue";
import DistortionReportDetail from "./report-details/DistortionReportDetail.vue";
// 进度条组件
import TaskProgressOverlay from "./TaskProgressOverlay.vue";
// 压缩包处理库
import JSZip from "jszip";
// Logo图片
import logoImage from "@/assets/yanding-logo.png";

export default {
  name: "ReportPanel",
  components: {
    ColorReproductionReportDetail,
    DynamicRangeReportDetail,
    SharpnessReportDetail,
    LuminanceUniformityReportDetail,
    DistortionReportDetail,
    TaskProgressOverlay,
  },

  props: {
    reports: { type: Array, required: true },
    isLoadingList: { type: Boolean, default: false },
    imageUploadCount: { type: Number, default: 0 },
    maxUploadPerDay: { type: Number, default: 0 },
  },
  data() {
    return {
      activeMenu: null,
      activeReport: null,
      isLoadingDetail: false,
      reportDetailData: null,
      pendingActiveKey: null,
      isDragging: false,
      startX: 0,
      scrollLeft: 0,
      canExportReport: false,
      downloadDialogVisible: false,
      checkAll: false,
      isIndeterminate: false,
      // checkedReports: [],
      downloadTypes: {},
      reportPollTimer: null,
      isDownloading: false,
      isExportingPdf: false,
      downloadProgressMessage: "",
      displayProgress: 0,
      realProgress: 0,
      animationFrameId: null,
      convertedThumbUrls: {},
      convertingThumbMap: {},
      convertErrors: {},
    };
  },
  computed: {
    ...mapGetters(["isLoggedIn"]),
    reportTitle() {
      if (!this.activeReport) return ""; // 如果没有激活的报告，返回空字符串

      switch (this.activeReport.analysis_type) {
        case "Color Reproduction":
          return this.$t("report.report_name1");
        case "Dynamic Range":
          return this.$t("report.report_name2");
        case "Sharpness":
          return this.$t("report.report_name3");
        case "Luminance Uniformity":
          return this.$t("report.report_name4");
        case "Distortion":
          return this.$t("report.report_name5");
        default:
          return this.$t("report.dataError");
      }
    },
    activeReportId() {
      return this.activeReport ? this.activeReport.image_id : null;
    },
    activeReportThumbnailUrl() {
      return this.activeReport ? this.activeReport.thumbnail_url : null;
    },
    scrollbarStyle() {
      const itemCount = this.downloadableReports.length;
      const itemHeight = 73;
      const threshold = 4;
      return itemCount > threshold
        ? { height: `${itemHeight * threshold}px` }
        : { height: "auto" };
    },
    currentMenuItems() {
      if (!this.activeReport || !this.activeReport.analysis_type) return [];
      const typeKey = this.activeReport.analysis_type.replace(/ /g, "");
      const i18nKey = `reportMenus.${typeKey}`;
      const menu = this.$t(i18nKey);
      return Array.isArray(menu) ? menu : [];
    },
    detailComponent() {
      if (!this.activeReport) return null;
      switch (this.activeReport.analysis_type) {
        case "Color Reproduction":
          return "ColorReproductionReportDetail";
        case "Dynamic Range":
          return "DynamicRangeReportDetail";
        case "Sharpness":
          return "SharpnessReportDetail";
        case "Luminance Uniformity":
          return "LuminanceUniformityReportDetail";
        case "Distortion":
          return "DistortionReportDetail";
        default:
          return null;
      }
    },
    downloadableReports() {
      return this.reports.filter((report) => report.status === "successed");
    },
    hasNoSelections() {
      // 检查是否至少有一个报告选中了至少一个类型
      return !this.downloadableReports.some((report) => {
        const types = this.downloadTypes[report.image_id] || [];
        return types.length > 0;
      });
    },
  },
  watch: {
    reports: {
      handler(newReports) {
        // 检查是否有 processing 状态的报告
        const hasProcessing =
          newReports && newReports.some((r) => r.status === "processing");

        if (hasProcessing) {
          // 有 processing 状态，开始轮询
          this.startReportPolling();
        } else {
          // 没有 processing 状态，停止轮询
          this.stopReportPolling();
        }

        if (newReports && newReports.length > 0) {
          const newIds = new Set(newReports.map((r) => r.image_id));
          Object.keys(this.convertedThumbUrls).forEach((id) => {
            if (!newIds.has(id)) {
              this.$delete(this.convertedThumbUrls, id);
              this.$delete(this.convertingThumbMap, id);
              this.$delete(this.convertErrors, id);
            }
          });

          newReports.forEach((report) => {
            if (report.thumbnail_url && this.isTifUrl(report.thumbnail_url)) {
              this.getThumbnailUrl(report);
            }
          });

          if (this.activeReport) {
            const updatedReport = newReports.find(
              (r) => r.image_id === this.activeReport.image_id
            );
            if (updatedReport) {
              // 如果状态变化了，需要重新加载
              if (this.activeReport.status !== updatedReport.status) {
                // 状态从 processing → successed/failed，需要重新加载
                this.setActiveReport(updatedReport);
              } else {
                // 只是数据刷新，直接更新
                this.activeReport = updatedReport;
              }
            } else {
              // activeReport 不在列表中了
              // 若当前激活项仍处于 processing，容忍后端短暂缺失，避免切换触发不必要的详情加载
              if (
                this.activeReport &&
                this.activeReport.status === "processing"
              ) {
                return;
              }
              this.setActiveReport(newReports[0]);
            }
          } else {
            // 没有 activeReport，选择第一个
            this.setActiveReport(newReports[0]);
          }
        } else {
          // 列表为空：若当前激活项处于 processing，保留激活，等待后续轮询恢复
          if (this.activeReport && this.activeReport.status === "processing") {
            return;
          }
          this.activeReport = null;
          this.reportDetailData = null;
          this.activeMenu = null;
        }
      },
      immediate: true,
    },
    // 旧代码：基于 checkedReports 计算全选状态（已改为基于 downloadTypes）
    // checkedReports(newValue) {
    //   const checkedCount = newValue.length;
    //   const totalCount = this.downloadableReports.length;
    //   this.checkAll = checkedCount === totalCount && totalCount > 0;
    //   this.isIndeterminate = checkedCount > 0 && checkedCount < totalCount;
    // },
    // 新代码：基于 downloadTypes 计算全选状态
    downloadTypes: {
      handler() {
        // 计算全选状态：所有报告都同时选中了PDF和CSV才算全选
        const totalCount = this.downloadableReports.length;
        if (totalCount === 0) {
          this.checkAll = false;
          this.isIndeterminate = false;
          return;
        }

        const fullySelectedCount = this.downloadableReports.filter((report) => {
          const types = this.downloadTypes[report.image_id] || [];
          return (
            types.length === 2 && types.includes("pdf") && types.includes("csv")
          );
        }).length;

        const partiallySelectedCount = this.downloadableReports.filter(
          (report) => {
            const types = this.downloadTypes[report.image_id] || [];
            return types.length > 0;
          }
        ).length;

        this.checkAll = fullySelectedCount === totalCount;
        this.isIndeterminate =
          partiallySelectedCount > 0 && fullySelectedCount < totalCount;
      },
      deep: true,
    },
    isLoggedIn: {
      handler(newVal, oldVal) {
        if (newVal === true && oldVal === false) {
          this.fetchUserPermissions();
        }
        if (newVal === false) {
          this.canExportReport = false;
        }
      },
      immediate: true,
    },
  },
  created() {
    if (this.isLoggedIn) this.fetchUserPermissions();
  },
  beforeDestroy() {
    this.stopReportPolling();
  },
  methods: {
    startReportPolling() {
      // 如果已经有定时器在运行，不重复启动
      if (this.reportPollTimer) return;

      this.reportPollTimer = setInterval(() => {
        this.$emit("refresh-reports"); // 通知父组件刷新
      }, 5000); // 每 5 秒刷新一次
    },

    stopReportPolling() {
      if (this.reportPollTimer) {
        clearInterval(this.reportPollTimer);
        this.reportPollTimer = null;
      }
    },

    async processSinglePdfDownload(item, timestamp) {
      try {
        this.isExportingPdf = true;
        await this.$nextTick();

        this.downloadProgressMessage = this.$t("sidebar.generatingPdf", {
          current: 1,
          total: 1,
          name: item.image_name,
        });

        await this.setActiveReportForExport(item);
        const pdf = await this.generatePdfObject(0, 100);

        const baseName =
          item.image_name.split(".").slice(0, -1).join(".") || item.image_id;
        const fileName = `Report_${baseName}_${item.image_id}_${timestamp}.pdf`;
        const pdfBlob = pdf.output("blob");

        this.triggerFileDownload(pdfBlob, fileName);
      } catch (error) {
        console.error(`单文件PDF下载失败 for ${item.image_name}:`, error);
        throw error;
      }
    },
    smoothAnimateProgress(target, duration = 1000) {
      this.cancelAnimation();

      const start = this.displayProgress;
      const change = target - start;
      if (Math.abs(change) < 0.1) {
        this.displayProgress = target;
        return;
      }

      let startTime = null;

      const animate = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const elapsedTime = currentTime - startTime;
        const progress = Math.min(1, elapsedTime / duration);
        const easedProgress = 1 - Math.pow(1 - progress, 3);

        this.displayProgress = start + change * easedProgress;

        if (elapsedTime < duration) {
          this.animationFrameId = requestAnimationFrame(animate);
        } else {
          this.displayProgress = target;
          this.animationFrameId = null;
        }
      };

      this.animationFrameId = requestAnimationFrame(animate);
    },

    cancelAnimation() {
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }
    },

    async fetchUserPermissions() {
      try {
        const response = await getUserInfo();
        if (response.success && response.data && response.data.settings) {
          this.canExportReport = response.data.settings.exportReport || false;
        }
      } catch (error) {
        console.error(
          "Failed to fetch user permissions for ReportPanel:",
          error
        );
        this.canExportReport = false;
      }
    },

    handleMenuClick(item) {
      this.activeMenu = item;
      let sectionId = null;
      const reportType = this.activeReport?.analysis_type;

      if (item === this.currentMenuItems[0]) {
        sectionId = "top";
      } else {
        switch (reportType) {
          case "Color Reproduction":
            {
              const analysisResultText = this.$t(
                "reportMenus.ColorReproduction[1]"
              );
              const coordinateValueText = this.$t(
                "reportMenus.ColorReproduction[2]"
              );
              const snrText = this.$t("reportMenus.ColorReproduction[3]");
              if (item === analysisResultText) sectionId = "analysis-result";
              else if (item === coordinateValueText)
                sectionId = "coordinate-value";
              else if (item === snrText) sectionId = "snr";
            }
            break;
          case "Dynamic Range":
            {
              const analysisResultText = this.$t("reportMenus.DynamicRange[1]");
              const oecfcurve = this.$t("reportMenus.DynamicRange[2]");
              const snr = this.$t("reportMenus.DynamicRange[3]");
              const snriso15739 = this.$t("reportMenus.DynamicRange[4]");
              const visualnoise = this.$t("reportMenus.DynamicRange[5]");
              const visualsnr = this.$t("reportMenus.DynamicRange[6]");
              if (item === analysisResultText) sectionId = "analysis-result";
              else if (item === oecfcurve) sectionId = "oecf-curve";
              else if (item === snr) sectionId = "snr";
              else if (item === snriso15739) sectionId = "snr-iso15739";
              else if (item === visualnoise) sectionId = "visual-noise";
              else if (item === visualsnr) sectionId = "visual-snr";
            }
            break;
          case "Sharpness":
            {
              const analysisResultText = this.$t("reportMenus.Sharpness[1]");
              const dataCurvesText = this.$t("reportMenus.Sharpness[2]");
              if (item === analysisResultText) sectionId = "analysis-result";
              else if (item === dataCurvesText) sectionId = "data-curves";
            }
            break;
          case "Luminance Uniformity":
            {
              const analysisResultText = this.$t(
                "reportMenus.LuminanceUniformity[1]"
              );
              const brightnessDistText = this.$t(
                "reportMenus.LuminanceUniformity[2]"
              );
              if (item === analysisResultText) sectionId = "analysis-result";
              else if (item === brightnessDistText)
                sectionId = "brightness-distribution";
            }
            break;
          case "Distortion":
            {
              const analysisResultText = this.$t("reportMenus.Distortion[1]");
              const brightnessDistText = this.$t("reportMenus.Distortion[2]");
              if (item === analysisResultText) sectionId = "analysis-result";
              else if (item === brightnessDistText)
                sectionId = "brightness-distribution";
            }
            break;
        }
      }
      if (sectionId && this.$refs.detailComponentRef) {
        this.$nextTick(() => {
          if (
            typeof this.$refs.detailComponentRef.scrollToSection === "function"
          ) {
            this.$refs.detailComponentRef.scrollToSection(sectionId);
          }
        });
      }
    },

    triggerUpload() {
      this.$emit("add-images");
    },

    async setActiveReport(report) {
      if (!report) return;

      // 生成本次请求的唯一键（按图片与状态）
      const nextKey = `${report.image_id}:${report.status}`;

      // 若当前激活项与目标相同（同图同状态），直接返回
      if (
        this.isDownloading === false &&
        this.activeReport &&
        this.activeReport.image_id === report.image_id &&
        this.activeReport.status === report.status
      )
        return;

      // 同步更新激活项与菜单
      this.activeReport = report;
      this.$nextTick(() => {
        this.activeMenu = this.currentMenuItems[0] || null;
      });
      if (this.isDownloading === false)
        this.$emit("report-selected", report.analysis_type);

      // 非 successed 不发起详情请求：直接清空并退出（不设置 pendingActiveKey）
      if (report.status !== "successed") {
        this.reportDetailData = null;
        this.isLoadingDetail = false;
        // 若有旧的详情请求在进行，清空 pending key，避免旧响应回写当前页面
        this.pendingActiveKey = null;
        return;
      }

      // 并发去重：若已有相同 key 在进行，直接返回；若不同则覆盖为新的 key
      if (this.pendingActiveKey === nextKey) return;
      this.pendingActiveKey = nextKey;

      this.isLoadingDetail = true;
      this.reportDetailData = null;
      try {
        const response = await getAnalysisDetail(report.image_id);
        // 仅当本次响应仍对应当前的 pending key 时才允许落地
        if (
          this.pendingActiveKey === nextKey &&
          response.success &&
          response.data
        ) {
          this.reportDetailData = response.data;
        }
      } catch (error) {
        console.log("请求失败，但错误已被捕获，页面不会崩溃。", error);
      } finally {
        // 仅当 key 未被后续请求覆盖时才清理 loading 与 key
        if (this.pendingActiveKey === nextKey) {
          this.isLoadingDetail = false;
          this.pendingActiveKey = null;
        }
      }
    },

    statusClass(status) {
      return `status-${status}`;
    },

    handleMouseDown(e) {
      this.isDragging = true;
      const scrollWrapper = this.$refs.scrollWrapper;
      this.startX = e.pageX - scrollWrapper.offsetLeft;
      this.scrollLeft = scrollWrapper.scrollLeft;
      scrollWrapper.classList.add("is-grabbing");
    },

    handleMouseMove(e) {
      if (!this.isDragging) return;
      e.preventDefault();
      const scrollWrapper = this.$refs.scrollWrapper;
      const x = e.pageX - scrollWrapper.offsetLeft;
      const walk = (x - this.startX) * 2;
      scrollWrapper.scrollLeft = this.scrollLeft - walk;
    },

    handleMouseUp() {
      if (!this.isDragging) return;
      this.isDragging = false;
      if (this.$refs.scrollWrapper)
        this.$refs.scrollWrapper.classList.remove("is-grabbing");
    },

    handleMouseLeave() {
      if (!this.isDragging) return;
      this.isDragging = false;
      if (this.$refs.scrollWrapper)
        this.$refs.scrollWrapper.classList.remove("is-grabbing");
    },

    openDownloadDialog() {
      // 旧代码：初始化 checkedReports（已不再使用）
      // this.checkedReports = [];
      const initialTypes = {};
      this.downloadableReports.forEach((report) => {
        // 初始化为空数组，不选中任何选项
        initialTypes[report.image_id] = [];
      });
      this.downloadTypes = initialTypes;
      this.downloadDialogVisible = true;
    },

    handleCheckAllChange(val) {
      // 旧代码：基于 checkedReports 的全选逻辑（已改为基于 downloadTypes）
      // this.checkedReports = val
      //   ? this.downloadableReports.map((r) => r.image_id)
      //   : [];

      // 新代码：全选时将所有报告的PDF和CSV都选中；取消全选时清空所有
      const allTypes = val ? ["pdf", "csv"] : [];
      this.downloadableReports.forEach((report) => {
        this.$set(this.downloadTypes, report.image_id, [...allTypes]);
      });
    },

    // 旧代码：切换单个报告的选择状态（已不再使用，因为移除了报告选择复选框）
    // toggleReportSelection(reportId) {
    //   const index = this.checkedReports.indexOf(reportId);
    //   if (index > -1) {
    //     this.checkedReports.splice(index, 1);
    //   } else {
    //     this.checkedReports.push(reportId);
    //   }
    // },

    async handleDownload() {
      this.isDownloading = true;
      this.realProgress = 0;
      this.displayProgress = 0;
      this.downloadProgressMessage = this.$t("sidebar.preparingDownload");
      this.$emit("download-state-change", true);
      this.downloadDialogVisible = false;

      const originalState = {
        activeReport: this.activeReport,
        reportDetailData: this.reportDetailData,
        activeMenu: this.activeMenu,
      };

      // 旧代码：基于 checkedReports 筛选选中项（已改为基于 downloadTypes）
      // const selectedItems = this.downloadableReports
      //   .filter((r) => this.checkedReports.includes(r.image_id))
      //   .map((r) => ({ ...r, selectedType: this.downloadTypes[r.image_id] }));

      // 新代码：基于 downloadTypes 构建选中项，每个报告可能包含多个类型
      const allSelectedItems = [];
      this.downloadableReports.forEach((report) => {
        const types = this.downloadTypes[report.image_id] || [];
        types.forEach((type) => {
          allSelectedItems.push({ ...report, selectedType: type });
        });
      });

      if (allSelectedItems.length === 0) {
        this.isDownloading = false;
        this.$emit("download-state-change", false);
        return;
      }

      const pdfRequests = allSelectedItems.filter(
        (item) => item.selectedType === "pdf"
      );

      // 检查是否有包含TIF图片的报告
      const isMobile = window.innerWidth <= 768;

      // 手机端：检查是否有TIF报告被选择（不管是PDF还是CSV），且同时还有其他报告被选择
      if (isMobile) {
        // 获取所有唯一的报告ID
        const uniqueReportIds = [
          ...new Set(allSelectedItems.map((item) => item.image_id)),
        ];

        // 如果只有一个报告被选择，允许（即使同时选择了PDF和CSV）
        if (uniqueReportIds.length > 1) {
          const hasTifReports = await this.checkTifReportsSelected(
            allSelectedItems
          );
          // 如果手机端有TIF报告，且同时还有其他报告被选择（不管是PDF还是CSV），报错
          if (hasTifReports) {
            this.$notify.warning({
              title: this.$t("error.errorTitle"),
              message: this.$t("error.mobileTifExportLimit"),
              duration: 5000,
            });
            this.isDownloading = false;
            this.isExportingPdf = false;
            this.$emit("download-state-change", false);
            return;
          }
        }
      }

      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      const hours = String(now.getHours()).padStart(2, "0");
      const minutes = String(now.getMinutes()).padStart(2, "0");
      const seconds = String(now.getSeconds()).padStart(2, "0");
      const timestamp = `${year}${month}${day}_${hours}${minutes}${seconds}`;

      try {
        const tasks = [];
        tasks.push(this.handleBackendDownloadRequest(allSelectedItems));

        if (pdfRequests.length === 1) {
          tasks.push(this.processSinglePdfDownload(pdfRequests[0], timestamp));
        } else if (pdfRequests.length > 1) {
          tasks.push(this.processPdfDownloads(pdfRequests, timestamp));
        }

        await Promise.all(tasks);

        await this.smoothAnimateProgress(100, 300);
        this.downloadProgressMessage = this.$t("success.allDownloadsComplete");
        this.$notify.success({
          title: this.$t("success.successTitle"),
          message: this.$t("success.allDownloadsComplete"),
        });

        await new Promise((resolve) => setTimeout(resolve, 1000));
      } catch (error) {
        console.error("一个或多个下载任务失败:", error);
      } finally {
        Object.keys(this.convertingThumbMap).forEach((id) => {
          this.$delete(this.convertingThumbMap, id);
        });
        this.cancelAnimation();
        this.isDownloading = false;
        this.isExportingPdf = false;
        this.$emit("download-state-change", false);
        this.activeReport = originalState.activeReport;
        this.reportDetailData = originalState.reportDetailData;
        this.activeMenu = originalState.activeMenu;
        this.downloadProgressMessage = "";
      }
    },

    async handleBackendDownloadRequest(selectedItems) {
      const payload = {
        image_id: selectedItems.map((item) => item.image_id).join(","),
        type: selectedItems.map((item) => item.selectedType).join(","),
      };

      try {
        const response = await downloadReport(payload);
        const blob = response.data;

        if (blob.type.includes("application/json")) {
          const jsonText = await blob.text();
          const data = JSON.parse(jsonText);
          if (data.success) {
            console.log("后端请求成功（无文件返回）:", data.message);
          } else {
            this.$notify.error({
              title: this.$t("error.errorTitle"),
              message: data.message || this.$t("error.errorDownload"),
            });
            throw new Error(data.message);
          }
        } else if (blob && blob.size > 0) {
          const contentDisposition = response.headers["content-disposition"];
          let filename = `reports.zip`;
          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
            if (filenameMatch && filenameMatch.length > 1) {
              filename = decodeURIComponent(filenameMatch[1]);
            }
          }
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.setAttribute("download", filename);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(link.href);
        } else {
          const hasCsv = selectedItems.some(
            (item) => item.selectedType === "csv"
          );
          if (hasCsv) {
            throw new Error(
              "Received empty file stream when one was expected for CSV download."
            );
          }
          console.log(
            "Backend request completed without returning a file, which is expected for PDF-only requests."
          );
        }
      } catch (error) {
        console.error("后端下载请求失败:", error.response || error);
        this.$notify.error({
          title: this.$t("error.errorTitle"),
          message: error.message || this.$t("error.errorDownload"),
        });
        throw error;
      }
    },

    async processPdfDownloads(pdfRequests, timestamp = null) {
      this.isExportingPdf = true;
      await this.$nextTick();

      const zip = new JSZip();
      const totalReports = pdfRequests.length;
      let processedReports = 0;

      if (!timestamp) {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, "0");
        const day = String(now.getDate()).padStart(2, "0");
        const hours = String(now.getHours()).padStart(2, "0");
        const minutes = String(now.getMinutes()).padStart(2, "0");
        const seconds = String(now.getSeconds()).padStart(2, "0");
        timestamp = `${year}${month}${day}_${hours}${minutes}${seconds}`;
      }

      for (const item of pdfRequests) {
        this.downloadProgressMessage = this.$t("sidebar.generatingPdf", {
          current: processedReports + 1,
          total: totalReports,
          name: item.image_name,
        });

        const progressStart = (processedReports / totalReports) * 100;
        const progressEnd = ((processedReports + 1) / totalReports) * 100;

        try {
          await this.setActiveReportForExport(item);
          const pdf = await this.generatePdfObject(progressStart, progressEnd);

          const baseName =
            item.image_name.split(".").slice(0, -1).join(".") || item.image_id;
          const fileName = `Report_${baseName}_${item.image_id}_${timestamp}.pdf`;

          const pdfBlob = pdf.output("blob");
          zip.file(fileName, pdfBlob);
        } catch (e) {
          console.error(`处理 ${item.image_name} 的PDF时出错:`, e);
          this.$notify.error({
            title: this.$t("error.errorTitle"),
            message: this.$t("error.sendReportError", {
              image_name: item.image_name,
            }),
          });
          this.realProgress = progressEnd;
          await this.smoothAnimateProgress(this.realProgress);
        } finally {
          processedReports++;
        }
      }

      if (Object.keys(zip.files).length > 0) {
        this.downloadProgressMessage = this.$t("sidebar.zippingReports");
        const zipFileName = `Reports_${timestamp}.zip`;

        const zippingPromise = zip.generateAsync({ type: "blob" });
        const minimumDelayPromise = new Promise((resolve) =>
          setTimeout(resolve, 2000)
        );
        const [zipBlob] = await Promise.all([
          zippingPromise,
          minimumDelayPromise,
        ]);

        this.triggerFileDownload(zipBlob, zipFileName);
      }
    },

    async setActiveReportForExport(reportItem) {
      const response = await getAnalysisDetail(reportItem.image_id);
      if (!response.success || !response.data) {
        throw new Error(`无法获取 ${reportItem.image_name} 的报告详情`);
      }
      this.activeReport = reportItem;
      this.reportDetailData = response.data;
      await this.$nextTick();
    },

    async waitForImagesToLoad(element) {
      const images = Array.from(element.querySelectorAll("img"));
      if (images.length === 0) return Promise.resolve();

      // 特别处理TIF转换后的Data URL图片
      const promises = images.map((img) => {
        return new Promise((resolve) => {
          // 如果是Data URL（TIF转换后的图片），需要特别处理
          if (img.src && img.src.startsWith("data:image")) {
            // 检查图片是否已加载
            if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
              // 额外等待，确保图片完全渲染
              setTimeout(() => resolve(), 200);
            } else {
              // 等待图片加载，最多等待10秒
              const timeout = setTimeout(() => {
                console.warn("Data URL图片加载超时");
                resolve(); // 超时也继续，避免阻塞
              }, 10000);

              img.onload = () => {
                clearTimeout(timeout);
                // 加载后额外等待，确保渲染完成
                setTimeout(() => resolve(), 300);
              };
              img.onerror = () => {
                clearTimeout(timeout);
                console.warn("Data URL图片加载失败");
                resolve(); // 错误也继续
              };

              // 如果src已设置但还没触发onload，强制触发检查
              if (img.src && !img.complete) {
                // 重新设置src可能触发加载
                const currentSrc = img.src;
                img.src = "";
                setTimeout(() => {
                  img.src = currentSrc;
                }, 10);
              }
            }
          } else if (img.complete && img.naturalWidth > 0) {
            resolve();
          } else {
            const timeout = setTimeout(() => {
              console.warn("图片加载超时");
              resolve();
            }, 10000);

            img.onload = () => {
              clearTimeout(timeout);
              resolve();
            };
            img.onerror = () => {
              clearTimeout(timeout);
              console.warn("图片加载失败");
              resolve();
            };
          }
        });
      });

      await Promise.all(promises);
      // 额外等待，确保所有图片（特别是Data URL）完全渲染
      await new Promise((resolve) => setTimeout(resolve, 500));
    },

    async loadLogoImage() {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          try {
            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0);
            const base64 = canvas.toDataURL("image/png");
            resolve(base64);
          } catch (error) {
            reject(error);
          }
        };
        img.onerror = () => {
          reject(new Error("Failed to load logo image"));
        };
        img.src = logoImage;
      });
    },

    addHeaderFooter(pdf, pageNumber, logoData) {
      if (!logoData) return;

      // 切换到指定页面
      pdf.setPage(pageNumber);

      // 页面尺寸（A4: 210mm x 297mm）
      const pageWidth = 210;
      const pageHeight = 297;
      const margins = { top: 15, right: 10, bottom: 15, left: 10 };

      // 添加页眉Logo（右上角）
      try {
        const logoHeight = 10; // Logo高度10mm
        const logoProps = pdf.getImageProperties(logoData);
        const logoWidth = (logoProps.width * logoHeight) / logoProps.height;
        const logoX = pageWidth - margins.right - logoWidth;
        const logoY = 5; // 距离顶部5mm

        pdf.addImage(logoData, "PNG", logoX, logoY, logoWidth, logoHeight);
      } catch (error) {
        console.warn("Failed to add logo to PDF:", error);
      }

      // 添加页脚页码（右下角）
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      const pageText = `${pageNumber}`;
      const textX = pageWidth - 6;
      const textY = pageHeight - 4;
      pdf.text(pageText, textX, textY);
    },

    async generatePdfObject(startProgress, endProgress) {
      const detailComponent = this.$refs.detailComponentRef;
      if (!detailComponent) return null;

      if (typeof detailComponent.togglePrintingState === "function") {
        detailComponent.togglePrintingState(true);
      }
      if (typeof detailComponent.ensureChartsRendered === "function") {
        await detailComponent.ensureChartsRendered();
      }

      // 等待DOM更新，确保TIF图片转换完成并显示
      await this.$nextTick();
      await this.$nextTick(); // 双重nextTick确保DOM完全更新

      // 主动触发所有TIF图片的转换（如果还没有开始）
      if (detailComponent.handleImageSourceChange) {
        if (detailComponent.reportResultData) {
          if (
            detailComponent.reportResultData.image_url &&
            this.isTifUrl(detailComponent.reportResultData.image_url)
          ) {
            detailComponent.handleImageSourceChange(
              "original",
              detailComponent.reportResultData.image_url
            );
          }
          if (
            detailComponent.reportResultData.roi_image_url &&
            this.isTifUrl(detailComponent.reportResultData.roi_image_url)
          ) {
            detailComponent.handleImageSourceChange(
              "roi",
              detailComponent.reportResultData.roi_image_url
            );
          }
          if (
            detailComponent.reportResultData.analysis_image_url &&
            this.isTifUrl(detailComponent.reportResultData.analysis_image_url)
          ) {
            detailComponent.handleImageSourceChange(
              "analysis",
              detailComponent.reportResultData.analysis_image_url
            );
          }
        }
        if (detailComponent.reportData) {
          if (
            detailComponent.reportData.image_url &&
            this.isTifUrl(detailComponent.reportData.image_url)
          ) {
            detailComponent.handleImageSourceChange(
              "original",
              detailComponent.reportData.image_url
            );
          }
          if (
            detailComponent.reportData.roi_image_url &&
            this.isTifUrl(detailComponent.reportData.roi_image_url)
          ) {
            detailComponent.handleImageSourceChange(
              "roi",
              detailComponent.reportData.roi_image_url
            );
          }
        }
      }

      // 再次等待DOM更新
      await this.$nextTick();
      await this.$nextTick();

      const isMobile = window.innerWidth <= 768;
      const maxWaitTime = isMobile ? 30000 : 20000; // 手机端等待更长时间
      const checkInterval = 200;
      const startTime = Date.now();

      // 等待所有TIF图片转换完成
      if (detailComponent.convertingMap && detailComponent.convertedImageUrls) {
        while (Date.now() - startTime < maxWaitTime) {
          // 检查是否还有正在转换的图片
          const isConverting = Object.values(
            detailComponent.convertingMap
          ).some((val) => val === true);

          if (!isConverting) {
            // 检查所有TIF图片是否都已转换完成
            // 查找所有TIF URL
            const tifUrls = [];
            if (detailComponent.reportResultData) {
              if (
                detailComponent.reportResultData.image_url &&
                this.isTifUrl(detailComponent.reportResultData.image_url)
              ) {
                tifUrls.push("original");
              }
              if (
                detailComponent.reportResultData.roi_image_url &&
                this.isTifUrl(detailComponent.reportResultData.roi_image_url)
              ) {
                tifUrls.push("roi");
              }
              if (
                detailComponent.reportResultData.analysis_image_url &&
                this.isTifUrl(
                  detailComponent.reportResultData.analysis_image_url
                )
              ) {
                tifUrls.push("analysis");
              }
            }
            if (detailComponent.reportData) {
              if (
                detailComponent.reportData.image_url &&
                this.isTifUrl(detailComponent.reportData.image_url)
              ) {
                tifUrls.push("original");
              }
              if (
                detailComponent.reportData.roi_image_url &&
                this.isTifUrl(detailComponent.reportData.roi_image_url)
              ) {
                tifUrls.push("roi");
              }
            }

            // 检查所有TIF图片是否都已转换
            const allConverted = tifUrls.every((type) => {
              return (
                detailComponent.convertedImageUrls[type] &&
                detailComponent.convertedImageUrls[type].startsWith(
                  "data:image"
                )
              );
            });

            if (allConverted || tifUrls.length === 0) {
              break; // 所有转换完成
            }
          }

          await new Promise((resolve) => setTimeout(resolve, checkInterval));
        }
      }

      // 等待DOM再次更新，确保转换后的图片已渲染
      await this.$nextTick();
      await this.$nextTick();

      // 手机端：额外等待，确保TIF转换后的图片已更新到DOM
      if (isMobile) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        // 强制触发视图更新
        if (detailComponent.$forceUpdate) {
          detailComponent.$forceUpdate();
        }
        await this.$nextTick();
      }

      // 检查所有图片元素是否已加载完成
      const allImages = detailComponent.$el.querySelectorAll("img");
      const imagePromises = Array.from(allImages).map((img) => {
        return new Promise((resolve) => {
          // 如果是Data URL（TIF转换后的图片），需要特别处理
          if (img.src && img.src.startsWith("data:image")) {
            // 检查图片是否已加载
            if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
              // 额外等待，确保图片完全渲染
              setTimeout(() => resolve(), isMobile ? 500 : 200);
            } else {
              // 等待图片加载，手机端等待更长时间
              const timeout = setTimeout(
                () => {
                  console.warn("Data URL图片加载超时:", img.src);
                  // 手机端：尝试重新设置src来触发加载
                  if (isMobile && img.src) {
                    const currentSrc = img.src;
                    img.src = "";
                    setTimeout(() => {
                      img.src = currentSrc;
                      setTimeout(() => resolve(), 500);
                    }, 100);
                  } else {
                    resolve(); // 超时也继续
                  }
                },
                isMobile ? 15000 : 10000
              );

              img.onload = () => {
                clearTimeout(timeout);
                // 加载后额外等待，确保渲染完成
                setTimeout(() => resolve(), isMobile ? 500 : 300);
              };
              img.onerror = () => {
                clearTimeout(timeout);
                console.warn("Data URL图片加载失败:", img.src);
                // 手机端：尝试重新设置src
                if (isMobile && img.src) {
                  const currentSrc = img.src;
                  img.src = "";
                  setTimeout(() => {
                    img.src = currentSrc;
                    setTimeout(() => resolve(), 500);
                  }, 100);
                } else {
                  resolve(); // 错误也继续
                }
              };

              // 如果src已设置但还没触发onload，强制触发检查
              if (img.src && !img.complete) {
                // 重新设置src可能触发加载
                const currentSrc = img.src;
                img.src = "";
                setTimeout(() => {
                  img.src = currentSrc;
                }, 10);
              }
            }
          } else if (
            img.complete &&
            img.naturalWidth > 0 &&
            img.naturalHeight > 0
          ) {
            resolve();
          } else {
            const timeout = setTimeout(
              () => {
                console.warn("图片加载超时:", img.src);
                resolve();
              },
              isMobile ? 15000 : 10000
            );

            img.onload = () => {
              clearTimeout(timeout);
              resolve();
            };
            img.onerror = () => {
              clearTimeout(timeout);
              console.warn("图片加载失败:", img.src);
              resolve(); // 错误也继续
            };
          }
        });
      });

      await Promise.all(imagePromises);

      // 额外等待，确保图片完全渲染（手机端需要更长时间）
      await new Promise((resolve) =>
        setTimeout(resolve, isMobile ? 2000 : 1000)
      );

      // 手机端：再次检查所有Data URL图片是否都已加载
      if (isMobile) {
        const dataUrlImages = Array.from(allImages).filter(
          (img) => img.src && img.src.startsWith("data:image")
        );
        const allDataUrlLoaded = dataUrlImages.every(
          (img) => img.complete && img.naturalWidth > 0 && img.naturalHeight > 0
        );

        if (!allDataUrlLoaded) {
          console.warn("部分Data URL图片未完全加载，等待额外时间...");
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      }

      // 加载logo图片
      let logoData = null;
      try {
        logoData = await this.loadLogoImage();
      } catch (error) {
        console.warn("Failed to load logo, continuing without logo:", error);
      }

      const pdf = new jsPDF("p", "mm", "a4");
      const margins = { top: 15, right: 10, bottom: 15, left: 10 };
      const contentWidth = 210 - margins.left - margins.right;
      const pageHeight = 297;
      let currentY = margins.top;

      // 为第1页添加页眉页脚
      if (logoData) {
        this.addHeaderFooter(pdf, 1, logoData);
      }

      const reportBlocks =
        detailComponent.$el.querySelectorAll(".pdf-atomic-block");
      const totalBlocks = reportBlocks.length;
      if (totalBlocks === 0) {
        this.realProgress = endProgress;
        await this.smoothAnimateProgress(this.realProgress);
        return pdf;
      }

      const progressPerBlock = (endProgress - startProgress) / totalBlocks;
      let blockIndex = 0;

      for (const block of reportBlocks) {
        try {
          // 在生成PDF前，将Data URL图片转换为Blob URL，避免html2canvas的兼容性问题
          const dataUrlImages = block.querySelectorAll(
            'img[src^="data:image"]'
          );
          const blobUrlMap = new Map();

          for (const img of dataUrlImages) {
            try {
              const dataUrl = img.src;
              // 将Data URL转换为Blob，再转换为Blob URL
              const response = await fetch(dataUrl);
              const blob = await response.blob();
              const blobUrl = URL.createObjectURL(blob);
              blobUrlMap.set(img, { originalSrc: dataUrl, blobUrl });
              // 临时替换为Blob URL
              img.src = blobUrl;
            } catch (convertError) {
              console.warn(
                "Data URL转Blob URL失败，使用原始Data URL:",
                convertError
              );
            }
          }

          // 等待所有图片（包括新转换的Blob URL）加载完成
          await this.waitForImagesToLoad(block);

          // 手机端需要更长的等待时间，确保图片完全渲染
          const isMobile = window.innerWidth <= 768;
          await new Promise((resolve) =>
            setTimeout(resolve, isMobile ? 1000 : 500)
          );

          // 再次检查所有图片是否都已加载
          const blockImages = block.querySelectorAll("img");
          const blockImagePromises = Array.from(blockImages).map((img) => {
            return new Promise((resolve) => {
              if (
                img.complete &&
                img.naturalWidth > 0 &&
                img.naturalHeight > 0
              ) {
                resolve();
              } else {
                const timeout = setTimeout(
                  () => {
                    console.warn("块内图片加载超时:", img.src);
                    resolve();
                  },
                  isMobile ? 8000 : 5000
                );

                img.onload = () => {
                  clearTimeout(timeout);
                  setTimeout(() => resolve(), 200); // 加载后额外等待
                };
                img.onerror = () => {
                  clearTimeout(timeout);
                  console.warn("块内图片加载失败:", img.src);
                  resolve();
                };
              }
            });
          });

          await Promise.all(blockImagePromises);

          // 使用try-catch包装html2canvas，捕获可能的运行时错误
          let canvas = null;
          let retryCount = 0;
          const maxRetries = 2;

          while (!canvas && retryCount <= maxRetries) {
            try {
              canvas = await html2canvas(block, {
                scale: 2.5,
                useCORS: true,
                logging: false,
                backgroundColor: "#ffffff",
                allowTaint: false,
                // 增加图片加载超时时间
                imageTimeout: 20000,
                // 禁用foreignObjectRendering，可能对Data URL/Blob URL更友好
                foreignObjectRendering: false,
              });
            } catch (canvasError) {
              retryCount++;
              console.error(
                `html2canvas错误 (尝试 ${retryCount}/${maxRetries + 1}):`,
                canvasError
              );

              if (retryCount <= maxRetries) {
                // 等待后重试
                await new Promise((resolve) => setTimeout(resolve, 1000));
                // 再次等待图片加载
                await this.waitForImagesToLoad(block);
              } else {
                // 所有重试都失败，抛出错误
                throw new Error(`html2canvas生成失败: ${canvasError.message}`);
              }
            }
          }

          // 恢复原始Data URL并清理Blob URL
          for (const [img, { originalSrc, blobUrl }] of blobUrlMap.entries()) {
            try {
              img.src = originalSrc;
              URL.revokeObjectURL(blobUrl);
            } catch (cleanupError) {
              console.warn("清理Blob URL失败:", cleanupError);
            }
          }

          if (!canvas) {
            throw new Error("html2canvas生成失败：无法创建canvas");
          }

          const imgData = canvas.toDataURL("image/jpeg", 0.9);

          // 清理canvas以释放内存
          canvas.width = 0;
          canvas.height = 0;
          canvas = null;

          const imgProps = pdf.getImageProperties(imgData);
          const blockHeight = (imgProps.height * contentWidth) / imgProps.width;

          const footerHeight = 5;
          if (
            currentY > margins.top &&
            currentY + blockHeight > pageHeight - margins.bottom - footerHeight
          ) {
            pdf.addPage();
            currentY = margins.top;
            // 为新页面添加页眉页脚
            if (logoData) {
              const currentPage = pdf.internal.getNumberOfPages();
              this.addHeaderFooter(pdf, currentPage, logoData);
            }
          }

          pdf.addImage(
            imgData,
            "JPEG",
            margins.left,
            currentY,
            contentWidth,
            blockHeight
          );
          currentY += blockHeight + 5;

          blockIndex++;
          this.realProgress = startProgress + blockIndex * progressPerBlock;
          this.smoothAnimateProgress(this.realProgress);
        } catch (error) {
          console.error("生成PDF块时出错:", error);

          // 确保清理所有Blob URL（即使在错误情况下）
          const dataUrlImages = block.querySelectorAll('img[src^="blob:"]');
          for (const img of dataUrlImages) {
            try {
              if (img.src && img.src.startsWith("blob:")) {
                URL.revokeObjectURL(img.src);
              }
            } catch (cleanupError) {
              console.warn("清理Blob URL失败:", cleanupError);
            }
          }

          // 继续处理下一个块，不中断整个流程
          // 但记录错误以便调试
          this.$notify.warning({
            title: this.$t("error.pdfGenerationWarning"),
            message: this.$t("error.pdfBlockError", { message: error.message }),
            duration: 3000,
          });
          continue;
        }
      }

      if (typeof detailComponent.togglePrintingState === "function") {
        detailComponent.togglePrintingState(false);
      }

      return pdf;
    },

    triggerFileDownload(blob, fileName) {
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);
    },
    isTifUrl(url) {
      if (!url) return false;
      const lower = url.toLowerCase();
      return lower.endsWith(".tif") || lower.endsWith(".tiff");
    },
    /**
     * 检查选中的报告列表中是否包含TIF图片（包括PDF和CSV）
     * @param {Array} selectedItems - 选中的报告项列表（包含selectedType）
     * @returns {Promise<boolean>} - 是否包含TIF图片
     */
    async checkTifReportsSelected(selectedItems) {
      if (!selectedItems || selectedItems.length === 0) return false;

      // 获取所有唯一的报告ID
      const uniqueReportIds = [
        ...new Set(selectedItems.map((item) => item.image_id)),
      ];

      // 首先检查每个报告的缩略图URL（快速检查）
      for (const reportId of uniqueReportIds) {
        const report = this.downloadableReports.find(
          (r) => r.image_id === reportId
        );
        if (
          report &&
          report.thumbnail_url &&
          this.isTifUrl(report.thumbnail_url)
        ) {
          return true;
        }
      }

      // 如果缩略图检查不到，尝试获取第一个报告的详情来检查
      if (uniqueReportIds.length > 0) {
        try {
          const firstReportId = uniqueReportIds[0];
          const response = await getAnalysisDetail(firstReportId, {
            showLoading: false,
          });
          if (response && response.success && response.data) {
            const reportData = response.data;
            // 检查报告中的图片URL
            if (
              (reportData.image_url && this.isTifUrl(reportData.image_url)) ||
              (reportData.image_roi_url &&
                this.isTifUrl(reportData.image_roi_url)) ||
              (reportData.analysis_image_url &&
                this.isTifUrl(reportData.analysis_image_url)) ||
              (reportData.roi_image_url &&
                this.isTifUrl(reportData.roi_image_url))
            ) {
              return true;
            }
          }
        } catch (error) {
          console.warn("检查TIF报告时出错:", error);
          // 如果检查失败，为了安全起见，假设可能包含TIF
          return true;
        }
      }

      return false;
    },

    /**
     * 检查报告列表中是否包含TIF图片
     * @param {Array} reports - 报告列表
     * @returns {Promise<boolean>} - 是否包含TIF图片
     */
    async checkTifReports(reports) {
      if (!reports || reports.length === 0) return false;

      // 首先检查每个报告的缩略图URL（快速检查）
      for (const report of reports) {
        if (report.thumbnail_url && this.isTifUrl(report.thumbnail_url)) {
          return true;
        }
      }

      // 如果缩略图检查不到，尝试获取第一个报告的详情来检查
      // 但为了性能，只检查第一个报告
      if (reports.length > 0) {
        try {
          const firstReport = reports[0];
          const response = await getAnalysisDetail(firstReport.image_id);
          if (response && response.success && response.data) {
            const reportData = response.data;
            // 检查报告中的图片URL
            if (
              (reportData.image_url && this.isTifUrl(reportData.image_url)) ||
              (reportData.image_roi_url &&
                this.isTifUrl(reportData.image_roi_url)) ||
              (reportData.analysis_image_url &&
                this.isTifUrl(reportData.analysis_image_url)) ||
              (reportData.roi_image_url &&
                this.isTifUrl(reportData.roi_image_url))
            ) {
              return true;
            }
          }
        } catch (error) {
          console.warn("检查TIF报告时出错:", error);
          // 如果检查失败，为了安全起见，假设可能包含TIF
          return true;
        }
      }

      return false;
    },
    getThumbnailUrl(report) {
      if (!report || !report.thumbnail_url)
        return report?.thumbnail_url || null;

      const { image_id: id, thumbnail_url: originalUrl } = report;

      if (this.convertedThumbUrls[id]) {
        return this.convertedThumbUrls[id];
      }

      if (!this.isTifUrl(originalUrl)) {
        return originalUrl;
      }

      if (!this.convertingThumbMap[id]) {
        this.convertTifThumbnail(report);
      }

      return originalUrl;
    },
    isThumbnailConverting(report) {
      if (!report) return false;
      return (
        this.isTifUrl(report.thumbnail_url) &&
        Boolean(this.convertingThumbMap[report.image_id])
      );
    },
    async convertTifThumbnail(report) {
      if (!report || !report.image_id || !report.thumbnail_url) return;
      const { image_id: id, thumbnail_url: url } = report;

      if (this.convertingThumbMap[id]) return;

      this.$set(this.convertingThumbMap, id, true);
      this.$delete(this.convertErrors, id);

      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch thumbnail: ${response.statusText}`);
        }

        const arrayBuffer = await response.arrayBuffer();
        const ifds = UTIF.decode(arrayBuffer);
        if (!ifds.length) {
          throw new Error("TIF 文件中没有找到图像数据");
        }

        UTIF.decodeImage(arrayBuffer, ifds[0]);
        const width = ifds[0].width;
        const height = ifds[0].height;

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");

        const imageData = ctx.createImageData(width, height);
        const rgba = UTIF.toRGBA8(ifds[0]);
        imageData.data.set(rgba);
        ctx.putImageData(imageData, 0, 0);

        const dataUrl = canvas.toDataURL("image/png");
        this.$set(this.convertedThumbUrls, id, dataUrl);
      } catch (error) {
        console.error(`转换 TIF 缩略图失败 ${id}:`, error);
        this.$set(this.convertErrors, id, error.message || "convert failed");
        this.$set(this.convertedThumbUrls, id, url);
      } finally {
        this.$delete(this.convertingThumbMap, id);
      }
    },
  },
};
</script>

<style scoped>
.report-panel-container {
  display: flex;
  height: 100%;
  width: 100%;
  gap: 15px;
  padding-left: 15px;
  box-sizing: border-box;
  padding-right: 10%;
}
.report-list-section {
  width: 260px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background-color: #fff;
  border-radius: 8px;
  padding: 20px;
  min-width: 0;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
}
.report-preview {
  width: 100%;
  height: 166px;
  background-color: #f7f8fa;
  border-radius: 6px;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  position: relative;
}
.report-preview img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
.report-thumb-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.78);
  z-index: 5;
  pointer-events: none;
}
.report-thumb-loading .el-icon-loading {
  font-size: 32px;
  color: #409eff;
}
.report-thumb-loading--compact {
  background-color: rgba(255, 255, 255, 0.6);
}
.report-thumb-loading--compact .el-icon-loading {
  font-size: 18px;
}
.no-report-view {
  flex-grow: 1;
  display: flex;
  justify-content: center;
  align-items: center;
}
.active-report-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  min-height: 22px;
  flex-shrink: 0;
}
.report-name {
  font-size: 14px;
  color: #303133;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.status-badge {
  padding: 2px 8px;
  font-size: 10px;
  color: #fff;
  border-radius: 8px;
  font-weight: bold;
  flex-shrink: 0;
}
.thumbnail-scroll-wrapper {
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  margin-bottom: 25px;
  cursor: grab;
  user-select: none;
  flex-shrink: 0;
}
.thumbnail-scroll-wrapper.is-grabbing {
  cursor: grabbing;
}
.thumbnail-scroll-wrapper::-webkit-scrollbar {
  height: 4px;
}
.thumbnail-scroll-wrapper::-webkit-scrollbar-thumb {
  background: #ddd;
  border-radius: 2px;
}
.thumbnail-scroll-wrapper::-webkit-scrollbar-track {
  background: transparent;
}
.thumbnail-scroll-wrapper {
  -ms-overflow-style: auto;
  scrollbar-width: thin;
}
.thumbnail-list {
  display: flex;
  gap: 15px;
  padding-bottom: 5px;
}
.thumbnail-item {
  flex: 0 0 80px;
  cursor: pointer;
  text-align: center;
  height: 64px; /* 固定高度：60px 图片 + 4px padding */
  overflow: hidden; /* 隐藏超出部分，防止图片加载失败时拉伸 */
  flex-shrink: 0; /* 防止被压缩 */
  position: relative; /* 为错误覆盖层提供定位基准 */
}
.image-container {
  position: relative;
  border-radius: 6px;
  border: 2px solid transparent;
  transition: all 0.3s;
  padding: 2px;
  height: 64px; /* 固定高度，与图片高度一致 */
  box-sizing: border-box; /* padding 包含在高度内 */
}
.image-container .report-thumb-loading {
  border-radius: 6px;
}
.thumbnail-item.active .image-container {
  border-color: #00a0e9;
  box-shadow: 0 0 8px rgba(0, 160, 233, 0.4);
}
.thumbnail-item img {
  width: 100%;
  height: 56px;
  border-radius: 4px;
  object-fit: cover;
  display: block;
}
.thumbnail-error-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(39, 38, 38, 0.7);
  color: rgba(245, 108, 108, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px; /* 缩略图较小，字体稍小 */
  pointer-events: none;
  z-index: 1;
  border-radius: 6px; /* 与 image-container 的 border-radius 一致 */
}
.divider {
  height: 1px;
  background-color: #e6ebf5;
  margin-bottom: 20px;
  flex-shrink: 0;
}
.report-menu {
  list-style: none;
  padding: 0;
  margin: 0 0 20px 0;
  flex-grow: 1;
  overflow-y: auto;
  min-height: 100px;
  flex-shrink: 1;
}
.report-menu li {
  padding: 10px 0;
  cursor: pointer;
  position: relative;
  transition: color 0.3s, font-weight 0.3s;
  color: #909399;
  font-size: 14px;
  font-weight: 500;
}
.report-menu li:hover {
  color: #00a0e9;
}
.report-menu li.active {
  color: #303133;
  font-weight: 700;
}
.report-menu li.active::after {
  content: "";
  position: absolute;
  bottom: 5px;
  left: 0;
  width: 24px;
  height: 2px;
  background-color: #00a0e9;
}
.status-successed {
  background-color: #67c23a;
}
.status-failed {
  background-color: #f56c6c;
}
.status-processing {
  background-color: #e6a23c;
}
.report-actions {
  padding-top: 15px;
  padding-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex-shrink: 0;
  min-height: 120px;
  position: relative;
  z-index: 10;
}
.report-actions .el-button {
  width: 100%;
  margin: 0;
  background-color: #00a0e9;
  border-color: #00a0e9;
  position: relative;
  z-index: 11;
}
.report-actions .el-button ::v-deep i {
  font-size: 16px;
  margin-right: 8px;
}
.report-actions .el-button:hover {
  background-color: #33b7f5;
  border-color: #33b7f5;
}
.upload-usage-hint {
  font-size: 12px;
  color: #909399;
  text-align: center;
  padding: 2px 0;
}
.report-detail-section {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  background-color: #fff;
  border-radius: 8px;
  padding: 16px;
  min-width: 0;
}
.report-detail-empty {
  flex-grow: 1;
  display: flex;
  justify-content: center;
  align-items: center;
}
.detail-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 15px;
  border-bottom: 1px solid #e6ebf5;
  flex-shrink: 0;
}
.header-title-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.header-title-group .main-title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: #303133;
}
.header-title-group .sub-title {
  margin: 0;
  font-size: 15px;
  font-weight: 500;
  color: #909399;
}
.detail-header .original-file-btn {
  background-color: #00a0e9;
  border-color: #00a0e9;
}
.detail-header .original-file-btn:hover,
.detail-header .original-file-btn:focus,
.detail-header .original-file-btn:active {
  background-color: #00a0e9;
  border-color: #00a0e9;
}
.detail-header .original-file-btn ::v-deep span {
  font-size: 14px;
  font-weight: 600;
}
.detail-content {
  flex-grow: 1;
  margin-top: 15px;
  min-height: 0;
}
.detail-scrollbar {
  height: 100%;
}
.detail-scrollbar ::v-deep .el-scrollbar__wrap {
  overflow-x: hidden;
}
@media (max-width: 768px) {
  .report-panel-container {
    flex-direction: column;
    height: auto;
    gap: 0;
    padding: 0;
  }
  .report-list-section {
    width: auto;
    border-radius: 8px 8px 0 0;
  }
  .report-detail-section {
    margin-top: 15px;
    min-height: 50vh;
    margin-bottom: 15px;
  }
  .header-title-group .main-title {
    font-size: 18px;
  }
  .header-title-group .sub-title {
    font-size: 13px;
  }
  .detail-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  .detail-header .original-file-btn {
    width: 100%;
  }
  .detail-header .original-file-btn ::v-deep span {
    display: inline-block;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    box-sizing: border-box;
  }

  .download-dialog ::v-deep .el-dialog {
    width: 90%;
  }
  .report-item .report-image-name {
    width: 120px;
  }
}

.download-dialog ::v-deep .el-dialog {
  width: 600px;
  max-width: 95%;
  border-radius: 12px;
}

.download-dialog ::v-deep .el-dialog__header {
  text-align: left;
  border-bottom: 1px solid #ebeef5;
  font-weight: 600;
}
.download-dialog ::v-deep .el-dialog__footer {
  text-align: center;
}

.download-dialog ::v-deep .el-dialog__body {
  padding: 20px 25px;
}

.all-report-checkbox {
  margin-bottom: 15px;
}
.all-report-checkbox ::v-deep .el-checkbox__label {
  font-weight: 500;
}

.report-list-scrollbar {
  background-color: #f7f8fa;
  border-radius: 8px;
}

.report-list-scrollbar ::v-deep .el-scrollbar__wrap {
  overflow-x: hidden;
  padding: 0 20px 15px 20px;
}

.report-list-scrollbar ::v-deep .el-scrollbar__thumb {
  background-color: rgba(144, 147, 153, 0.5);
  border-radius: 4px;
  transition: background-color 0.3s;
}
.report-list-scrollbar ::v-deep .el-scrollbar__thumb:hover {
  background-color: rgba(144, 147, 153, 0.7);
}

.report-item {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #ebeef5;
}
.report-item:last-child {
  border-bottom: none;
}

/* 旧代码：报告项复选框样式（已不再使用） */
/* .report-item-checkbox {
  margin-right: 0;
  width: 50px;
  flex-shrink: 0;
} */

.report-item .report-index {
  font-weight: bold;
  margin-left: 0;
  width: 50px; /* 保持与之前checkbox相同的宽度，维持布局 */
  text-align: center;
  display: inline-block;
  flex-shrink: 0;
}

.report-item .report-thumbnail {
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 4px;
  margin: 0 12px;
}
.download-thumb-wrapper {
  position: relative;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.download-thumb-wrapper .report-thumb-loading {
  border-radius: 4px;
}
.report-item .report-image-name {
  width: 180px;
  font-size: 14px;
  color: #303133;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.download-type-radio {
  margin-left: auto;
  padding-left: 15px;
}

@media (max-width: 768px) {
  ::v-deep .detail-content .report-header-images .image-wrapper img {
    max-width: 80% !important;
    height: auto !important;
    display: block;
    margin-left: auto;
    margin-right: auto;
  }

  ::v-deep .el-table td,
  ::v-deep .el-table th {
    padding-top: 5px !important;
    padding-bottom: 5px !important;
  }

  ::v-deep .el-table .cell {
    line-height: 1 !important;
    font-size: 11px !important;
  }
}
</style>

<style>
.report-panel-container.pdf-export-mode {
  width: 1200px !important;
  height: auto !important;
  overflow: visible !important;

  flex-direction: row !important;
  gap: 15px !important;
  padding: 0 !important;
}

.pdf-export-mode .report-list-section {
  display: none !important;
}

.pdf-export-mode .report-detail-section {
  width: 100% !important;
  margin: 0 !important;
  min-height: auto !important;
  box-shadow: none !important;
  border: none !important;
  position: static !important;
}
.pdf-export-mode .report-detail-section .el-loading-mask {
  display: none !important;
}

.pdf-export-mode .detail-scrollbar .el-scrollbar__wrap {
  overflow: visible !important;
  height: auto !important;
}

.pdf-export-mode .report-header-images {
  flex-direction: row !important;
}

.pdf-export-mode .side-by-side-tables {
  flex-direction: row !important;
}

.pdf-export-mode .detail-header {
  flex-direction: row !important;
  align-items: center !important;
  gap: 0 !important;
}

.pdf-export-mode .detail-header .original-file-btn {
  width: auto !important;
}

.pdf-export-mode .el-table {
  font-size: 16px !important;
}
.pdf-export-mode .el-table th.el-table__cell,
.pdf-export-mode .el-table td.el-table__cell {
  padding-top: 10.5px !important;
  padding-bottom: 10.5px !important;
}
.pdf-export-mode .el-table th .cell {
  font-size: 18px !important;
  font-weight: 600 !important;
}
</style>
